/**
 * Centralized formatting utilities for the admin panel
 * These functions replace duplicated formatting logic across components
 */

/**
 * Format currency with compact notation (K/M suffix)
 * Handles negative values properly
 */
export const formatCurrency = (value: number): string => {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1000000) {
    return `${sign}$${(absValue / 1000000).toFixed(1)}M`;
  }
  if (absValue >= 1000) {
    return `${sign}$${(absValue / 1000).toFixed(1)}K`;
  }
  return `${sign}$${absValue.toFixed(0)}`;
};

/**
 * Format currency with full precision using Intl.NumberFormat
 * Returns format like "$1,234.56" (no abbreviations, exact cents — never rounded
 * to whole dollars). Use for line-item tables and KPI cards where the precise
 * value matters; chart axes/tooltips keep the compact formatter.
 */
export const formatCurrencyFull = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format currency with cents using Intl.NumberFormat
 * Returns format like "$1,234.56" (with 2 decimal places)
 */
export const formatCurrencyWithCents = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Format currency rounded to whole dollars using Intl.NumberFormat
 * Returns format like "$18,400" (no decimal places). Use for big serif
 * "stake" figures (V2 Sales page lean cards) where cents are noise.
 */
export const formatCurrencyWhole = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Format number with locale-aware thousands separators
 */
export const formatNumber = (value: number): string => {
  return value.toLocaleString('en-US');
};

/**
 * Format number with compact notation (K/M suffix, no currency symbol)
 */
export const formatCompactNumber = (value: number): string => {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toFixed(0);
};

/**
 * Format percentage with optional sign prefix
 */
export const formatPercent = (value: number, showSign = true): string => {
  const sign = showSign && value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

/**
 * Parse a date string safely for display.
 * YYYY-MM-DD strings are treated as local midnight (not UTC).
 * Full ISO strings with time/timezone are parsed normally.
 */
export const parseDateString = (date: string): Date => {
  // Date-only strings (YYYY-MM-DD) are parsed as UTC by JS spec.
  // Append T00:00:00 to force local timezone interpretation.
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return new Date(date + 'T00:00:00');
  }
  return new Date(date);
};

const CYCLE_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * The billing-cycle label for a settlement's BILLING_DATE, mirroring the
 * Finances page's cycle labeling (apps/api/src/finances/finances.labels.ts
 * `labelCycles`): a cycle is named for the month of (BILLING_DATE - 1 day),
 * because a cycle closing e.g. July 1 settles June's billing. UTC math
 * (billingDate is a pure YYYY-MM-DD string) so the label never shifts by
 * timezone. Returns e.g. "June 2026 cycle".
 */
export const formatBillingCycleLabel = (billingDate: string): string => {
  const d = new Date(`${billingDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return `${CYCLE_MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()} cycle`;
};

/**
 * Format a 'YYYY-MM' month string for display (e.g. "May 2026"). Used by the
 * Quarter/All-time Brand Report (peak-month tile, monthly trend axis) —
 * pure string parsing, no Date() construction, so there's no timezone risk.
 * Returns the raw input unchanged if it isn't a valid YYYY-MM string.
 */
export const formatMonthLabel = (month: string): string => {
  const match = /^(\d{4})-(\d{2})$/.exec(month);
  if (!match) return month;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (monthIndex < 0 || monthIndex > 11) return month;
  return `${CYCLE_MONTH_NAMES[monthIndex]} ${year}`;
};

/**
 * Format date for display (e.g., "Dec 24, 2024")
 */
export const formatDate = (date: string | null | undefined): string => {
  if (!date) return '-';
  const d = parseDateString(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format date and time for display (e.g., "Aug 3, 2026, 3:41 PM").
 * `formatDate` truncates to the calendar day; this is for the cases where
 * that truncation erases a real distinction — e.g. two records stamped a
 * few hours apart on the same day that need to render differently. Same
 * `null`/`undefined` -> '-' convention as `formatDate`.
 */
export const formatDateTime = (date: string | null | undefined): string => {
  if (!date) return '-';
  const d = parseDateString(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Format date in short format (e.g., "Dec 24")
 */
export const formatDateShort = (date: string | null | undefined): string => {
  if (!date) return '-';
  const d = parseDateString(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Get relative time string (e.g., "2 days ago", "Today")
 */
export const getRelativeTime = (date: string): string => {
  const now = new Date();
  const then = parseDateString(date);
  const diff = now.getTime() - then.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

/**
 * Format decimal as percentage (multiplies by 100)
 */
export const formatDecimalAsPercent = (value: number): string => {
  return `${(value * 100).toFixed(1)}%`;
};

/**
 * An instant as its US Eastern day ("Oct 1"), whatever the viewer's
 * timezone. Order times are Eastern on every v2 page (plan 2026-10-06).
 */
export const formatEasternDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/New_York',
  });

/** An instant as US Eastern day and time ("Oct 1, 9:05 PM"). */
export const formatEasternDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/New_York',
  });
